import { FormControlLabel, Grid, MenuItem, Switch, TextField, Typography } from '@mui/material';
import CpfInput from 'components/masked-input/CpfInput';
import React, { useEffect, useRef } from 'react';
import { Supplier } from 'types/supplier';
import { SupplierValidation } from '../validation/useSupplierValidation';
import CnpjInput from 'components/masked-input/CnpjInput';
import PhoneInput from 'components/masked-input/PhoneInput';
import { RecordSection } from 'components/record-shell/RecordShell';
import { asInputComponent } from 'components/masked-input/asInputComponent';

interface SupplierFormProps {
  supplier: Supplier;
  handleChange: (index: keyof Supplier, value: any) => void;
  validation: SupplierValidation;
}

const SupplierForm: React.FC<SupplierFormProps> = ({ supplier, handleChange, validation }) => {
  const inputs = {
    company_name: useRef<HTMLInputElement>(null),
    company_trade_name: useRef<HTMLInputElement>(null),
    document_number: useRef<HTMLInputElement>(null),
    person_type: useRef<HTMLSelectElement>(null),
  };

  useEffect(() => {
    const [key] = Object.keys(validation) as [keyof typeof inputs];

    if (!key) return;

    inputs[key].current?.focus();
  }, [validation]);

  return (
    <Grid container size={12} spacing={2} maxWidth={900}>
      <Grid size={12}>
        <Typography variant="subtitle2" fontWeight={700} color="primary">
          Identificação
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          inputRef={inputs.company_name}
          label="Razão social"
          placeholder="Digite a razão social"
          value={supplier.company_name}
          onChange={e => handleChange('company_name', e.target.value)}
          error={!!validation.company_name}
          helperText={validation.company_name}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 3 }}>
        <TextField
          select
          fullWidth
          inputRef={inputs.person_type}
          error={!!validation.person_type}
          helperText={validation.person_type}
          label="Tipo de pessoa"
          placeholder="Selecione o tipo de pessoa"
          onChange={e => handleChange('person_type', e.target.value)}
          value={supplier.person_type}
        >
          <MenuItem value="N">Física</MenuItem>
          <MenuItem value="L">Jurídica</MenuItem>
        </TextField>
      </Grid>

      <Grid size={{ xs: 12, md: 3 }}>
        <TextField
          fullWidth
          inputRef={inputs.document_number}
          error={!!validation.document_number}
          helperText={validation.document_number}
          label={supplier.person_type === 'N' ? 'CPF' : 'CNPJ'}
          placeholder={supplier.person_type === 'N' ? 'Digite o CPF' : 'Digite o CNPJ'}
          value={supplier.document_number}
          onChange={e => handleChange('document_number', e.target.value)}
          slotProps={{
            input: {
              inputComponent: supplier.person_type === 'N' ? asInputComponent(CpfInput) : asInputComponent(CnpjInput),
            },
          }}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          inputRef={inputs.company_trade_name}
          label="Nome fantasia"
          placeholder="Digite o nome fantasia"
          value={supplier.company_trade_name}
          onChange={e => handleChange('company_trade_name', e.target.value)}
          error={!!validation.company_trade_name}
          helperText={validation.company_trade_name}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          label="Categoria"
          placeholder="Ex.: Materiais cirúrgicos"
          value={supplier.category ?? ''}
          onChange={e => handleChange('category', e.target.value)}
        />
      </Grid>

      <Grid size={12}>
        <RecordSection title="Contato">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                label="E-mail"
                placeholder="Digite o e-mail"
                value={supplier.email ?? ''}
                onChange={e => handleChange('email', e.target.value)}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Telefone"
                placeholder="Digite o telefone"
                value={supplier.phone ?? ''}
                onChange={e => handleChange('phone', e.target.value)}
                slotProps={{
                  input: {
                    inputComponent: asInputComponent(PhoneInput),
                  },
                }}
              />
            </Grid>
          </Grid>
        </RecordSection>
      </Grid>

      <Grid size={12}>
        <FormControlLabel
          label="Ativo"
          control={
            <Switch checked={!!supplier.active} onChange={e => handleChange('active', e.target.checked ? 1 : 0)} />
          }
        />
      </Grid>
    </Grid>
  );
};

export default SupplierForm;
